/**
 * NDA Print / Legal Preview.
 *
 * This is the single source of truth for the rendered NDA — both the
 * on-screen legal preview (`/ndas/:id/print`) AND the server-side PDF
 * consume this component.  Data comes from ``GET /api/billing/nda/{id}/preview``:
 *
 * * If the NDA has been "Generated" (frozen ``legal_snapshot``), the
 *   endpoint returns the frozen content verbatim.
 * * Otherwise it composes a live preview from the versioned template +
 *   item snapshots captured on the NDA.
 *
 * The print stylesheet is REUSED from Proposal Builder (``REEDOS_PRINT_STYLES``)
 * so REEDOS documents look visually consistent across the platform.
 * Nothing here overrides Proposal Builder's own print behaviour.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { REEDOS_PRINT_STYLES as printStyles } from "../lib/reedosPrintStyles";
import { toast } from "sonner";

function _joinAddress(p) {
    return [p.address_line1, p.address_line2, p.city, p.state_name, p.country, p.pin_code]
        .filter(Boolean)
        .join(", ");
}

export default function NDAPrint() {
    const { id } = useParams();
    const [nda, setNda] = useState(null);
    const [preview, setPreview] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [n, p, c] = await Promise.all([
                    api.get(`/billing/nda/${id}`),
                    api.get(`/billing/nda/${id}/preview`),
                    api.get(`/billing/company-info`),
                ]);
                setNda(n.data);
                setPreview(p.data);
                setCompany(c.data || null);
                // Formal PDF metadata cleanliness: set document.title so the
                // Chromium-produced PDF's Title metadata reads as the formal
                // NDA identity, not the app's chrome ("REEDOS · Billing &
                // Commercial").  Only affects THIS route; the original title
                // is restored on unmount so the rest of the SPA is unaffected.
                const nda_no = n.data?.nda_number || id;
                document.title = `REEDOS NDA ${nda_no}`;
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load NDA preview"));
            } finally {
                setLoading(false);
            }
        })();
        const _priorTitle = typeof document !== "undefined" ? document.title : null;
        return () => {
            if (_priorTitle !== null && typeof document !== "undefined") {
                document.title = _priorTitle;
            }
        };
    }, [id]);

    if (loading) return <div className="p-8 text-sm text-slate-500">Loading NDA preview…</div>;
    if (!nda || !preview) return null;

    const ctx = preview.context || {};
    const party = ctx.party || {};
    const isFrozen = preview.source === "snapshot";
    const companyName = company?.legal_name || "REEDOS Software Solutions Pvt. Ltd.";
    const companyAddress = company
        ? [company.address_line1, company.address_line2, company.city, company.state_name, company.country, company.pin_code].filter(Boolean).join(", ")
        : "Mumbai, Maharashtra, India";
    const companyGST = company?.gstin || "";
    // Human-readable footer label: strip any embedded "(Standard vN)" suffix
    // from the template_name so the footer reads cleanly and doesn't
    // duplicate the version.  `template_code` on the DB stays untouched.
    const templateName = (ctx.template?.name || "").replace(/\s*\(standard\s*v\d+\)\s*/i, "").trim();
    const templateFooterLabel = templateName
        ? `Template: ${templateName} · v${ctx.template?.version || "?"}`
        : `Template: ${ctx.template?.code || ""} · v${ctx.template?.version || "?"}`;

    return (
        <div className="reedos-print-root bg-white min-h-screen" data-testid="nda-print-root">
            <style>{printStyles}</style>
            <style>{`
                .nda-doc { font-family: 'Georgia', 'Times New Roman', serif; color: #1f2937; padding: 32px 40px; max-width: 820px; margin: 0 auto; }
                .nda-doc h1 { font-size: 26px; letter-spacing: 0.6px; margin-bottom: 4px; text-transform: uppercase; text-align: center; }
                .nda-doc h2 { font-size: 15px; letter-spacing: 3px; text-align: center; color: #475569; margin-bottom: 32px; font-weight: 400; text-transform: uppercase; }
                .nda-doc h3 { font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase; color: #334155; margin: 24px 0 8px; }
                .nda-doc .nda-cover { border-top: 3px solid #0f172a; border-bottom: 1px solid #cbd5e1; padding: 24px 0; margin-bottom: 24px; }
                .nda-doc .nda-parties { display: flex; gap: 16px; justify-content: space-between; margin-top: 8px; }
                .nda-doc .nda-party-box { flex: 1; border: 1px solid #cbd5e1; padding: 12px 14px; border-radius: 6px; font-size: 12px; }
                .nda-doc .nda-party-box .role { font-size: 10px; letter-spacing: 1.5px; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
                .nda-doc .nda-party-box .name { font-weight: 700; color: #0f172a; font-size: 13px; }
                .nda-doc .clause { margin-bottom: 14px; page-break-inside: avoid; }
                .nda-doc .clause .heading { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; color: #0f172a; }
                .nda-doc .clause .body { font-size: 12px; line-height: 1.55; text-align: justify; color: #1e293b; white-space: pre-line; }
                .nda-doc .annex { page-break-before: auto; }
                .nda-doc .annex-title { text-align: center; font-size: 15px; letter-spacing: 3px; text-transform: uppercase; color: #0f172a; margin-bottom: 4px; font-weight: 700; page-break-after: avoid; }
                .nda-doc .annex-subtitle { text-align: center; font-size: 11px; color: #64748b; margin-bottom: 16px; page-break-after: avoid; }
                .nda-doc table.annex-table { width: 100%; border-collapse: collapse; font-size: 11px; page-break-inside: auto; }
                .nda-doc table.annex-table thead { display: table-header-group; }
                .nda-doc table.annex-table tbody { page-break-inside: auto; }
                .nda-doc table.annex-table tr { page-break-inside: avoid; page-break-after: auto; }
                .nda-doc table.annex-table th, .nda-doc table.annex-table td { border: 1px solid #cbd5e1; padding: 8px 10px; vertical-align: top; text-align: left; }
                .nda-doc table.annex-table th { background: #f1f5f9; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; color: #475569; }
                .nda-doc .sig-block { display: flex; gap: 32px; margin-top: 42px; page-break-inside: avoid; }
                .nda-doc .sig-col { flex: 1; }
                .nda-doc .sig-col .name-line { border-bottom: 1px solid #64748b; height: 36px; margin: 24px 0 6px; }
                .nda-doc .sig-col .label { font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #64748b; }
                .nda-doc .sig-col .party { font-weight: 700; font-size: 12px; color: #0f172a; margin-bottom: 4px; }
                .nda-doc .meta-row { display: flex; gap: 20px; font-size: 11px; color: #475569; margin-top: 6px; }
                .nda-doc .disclaimer { font-size: 10px; color: #78350f; background: #fefce8; border-left: 3px solid #f59e0b; padding: 10px 12px; margin-top: 24px; line-height: 1.5; }
                .nda-doc .doc-footer { text-align: center; color: #64748b; font-size: 9.5px; margin-top: 40px; padding-top: 12px; border-top: 1px solid #e2e8f0; }
                @media print { .no-print { display: none !important; } }
            `}</style>

            <div className="no-print p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="text-xs text-slate-600">
                    <strong>{ctx.nda_number}</strong> · {ctx.nda_type} NDA · {isFrozen ? "Generated (frozen)" : "Live preview (not yet generated)"}
                    <div className="mt-1 text-[11px] text-amber-800">
                        Browser Print is for informal review only.  For the formal contractual PDF (server-generated, no browser headers/footers), use <strong>Download REEDOS-issued NDA (PDF)</strong> on the NDA detail page.
                    </div>
                </div>
                <button
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-500 hover:bg-slate-600 text-white text-xs"
                    data-testid="nda-print-btn"
                    title="Browser Print — informal preview only"
                >
                    <Printer size={14} /> Print preview (informal)
                </button>
            </div>

            <div className="nda-doc" data-testid="nda-print-cover">
                {/* Cover */}
                <div className="nda-cover">
                    <h1>Non-Disclosure Agreement</h1>
                    <h2>{ctx.nda_type} NDA · {ctx.nda_number}</h2>
                    <div style={{ textAlign: "center", fontSize: 12, color: "#334155" }}>
                        Effective from <strong>{formatDate(ctx.effective_date)}</strong> and valid until <strong>{formatDate(ctx.valid_until)}</strong>
                    </div>
                    <div className="nda-parties">
                        <div className="nda-party-box" data-testid="nda-print-party-discloser">
                            <div className="role">Discloser</div>
                            <div className="name">{companyName}</div>
                            <div style={{ fontSize: 11, marginTop: 4 }}>{companyAddress}</div>
                            {companyGST && <div style={{ fontSize: 10, marginTop: 2 }}>GSTIN: {companyGST}</div>}
                        </div>
                        <div className="nda-party-box" data-testid="nda-print-party-recipient">
                            <div className="role">Recipient</div>
                            <div className="name">{party.party_display_name || party.party_name || "—"}</div>
                            {party.party_name && party.party_name !== party.party_display_name && (
                                <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>Legal: {party.party_name}</div>
                            )}
                            <div style={{ fontSize: 11, marginTop: 4 }}>{_joinAddress(party) || "—"}</div>
                            {party.contact_person && (
                                <div style={{ fontSize: 10, marginTop: 4, color: "#475569" }}>
                                    Attn: {party.contact_person}{party.contact_email ? ` · ${party.contact_email}` : ""}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <h3>Subject</h3>
                <p style={{ fontSize: 12, marginBottom: 12 }}>{ctx.subject}</p>

                {/* Clauses */}
                <div data-testid="nda-print-clauses">
                    {(ctx.clauses || []).map((c, idx) => (
                        <div key={`${c.heading}-${idx}`} className="clause">
                            <div className="heading">{idx + 1}. {c.heading}</div>
                            <div className="body">{c.body}</div>
                        </div>
                    ))}
                </div>

                <div className="disclaimer" data-testid="nda-print-disclaimer">
                    {ctx.disclaimer || (
                        "This NDA covers exchange of Confidential Information solely for evaluation and discussion of a potential business opportunity.  It does NOT grant any commercial right, software licence, data licence, production usage, resale, redistribution, sublicensing right, right to expose the information through any customer-facing product or API, or right to train artificial-intelligence or machine-learning models.  Any such right requires a separate definitive commercial agreement with REEDOS."
                    )}
                </div>

                {/* Signatures */}
                <div className="sig-block" data-testid="nda-print-signatures">
                    <div className="sig-col">
                        <div className="party">For {companyName}</div>
                        <div className="name-line" />
                        <div className="label">Authorised Signatory · Name / Designation / Date</div>
                    </div>
                    <div className="sig-col">
                        <div className="party">For {party.party_display_name || party.party_name || "the Recipient"}</div>
                        <div className="name-line" />
                        <div className="label">Authorised Signatory · Name / Designation / Date</div>
                    </div>
                </div>

                {/* Annexure A */}
                <div className="annex" data-testid="nda-print-annexure">
                    <div className="annex-title">{ctx.annexure_a?.title || "Annexure A · Confidential Information"}</div>
                    <div className="annex-subtitle">{ctx.annexure_a?.subtitle || ""}</div>
                    {(ctx.annexure_a?.rows || []).length === 0 ? (
                        <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 12 }}>
                            No items enumerated.
                        </div>
                    ) : (
                        (() => {
                            // Group Annexure rows by parent_category so hierarchical
                            // items (e.g. Mutual Fund (MF)) surface with their group
                            // heading in the PDF.  Legacy rows without parent_category
                            // fall into the "" bucket, which renders header-less.
                            const rows = ctx.annexure_a?.rows || [];
                            const buckets = [];
                            const seenMap = new Map();
                            for (const r of rows) {
                                const key = (r.parent_category || "").trim();
                                if (!seenMap.has(key)) {
                                    seenMap.set(key, buckets.length);
                                    buckets.push([key, []]);
                                }
                                buckets[seenMap.get(key)][1].push(r);
                            }
                            const hierarchical = buckets.some(([k]) => k);
                            return (
                                <table className="annex-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 40 }}>Sr. No.</th>
                                            <th style={{ width: 240 }}>Information / Dataset</th>
                                            <th>Description / Scope of Disclosure</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {buckets.map(([parent, group]) => (
                                            <React.Fragment key={parent || "__unclassified__"}>
                                                {parent && (
                                                    <tr data-testid={`nda-annex-parent-${parent.replace(/\s+/g, "_")}`}>
                                                        <td
                                                            colSpan={3}
                                                            style={{
                                                                background: "#f1f5f9",
                                                                fontWeight: 600,
                                                                fontSize: 11,
                                                                textTransform: "uppercase",
                                                                letterSpacing: 0.5,
                                                                color: "#334155",
                                                                padding: "6px 8px",
                                                            }}
                                                        >
                                                            {parent}
                                                        </td>
                                                    </tr>
                                                )}
                                                {group.map((r) => (
                                                    <tr key={r.sr_no} data-testid={`nda-annex-row-${r.sr_no}`}>
                                                        <td>{r.sr_no}</td>
                                                        <td>
                                                            <strong>{r.item_name || r.item}</strong>
                                                            {r.item_group && !parent ? (
                                                                <><br /><span style={{ fontSize: 10, color: "#64748b" }}>{r.item_group}</span></>
                                                            ) : null}
                                                        </td>
                                                        <td>{r.description}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            );
                        })()
                    )}
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 12, textAlign: "center" }}>
                        End of Annexure A.  This annexure forms an integral part of the NDA identified as {ctx.nda_number}.
                    </div>
                </div>

                <div className="doc-footer">
                    NDA {ctx.nda_number} · {templateFooterLabel}
                    {ctx.generated_at && <> · Generated {formatDate(ctx.generated_at)}</>}
                    <br />
                    This document is confidential and intended solely for the parties identified above.  © {companyName}.
                </div>
            </div>
        </div>
    );
}
